import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { getCategoryTranslator } from '../utils/categoryTranslation';

const CURRENCIES = ['ILS', 'USD', 'EUR', 'GEL'];

const TIME_PRESET_KEYS = ['today', 'week', 'month', 'year', 'custom'];

function FilterPanel({ isOpen, onClose, filters, onApply, categories }) {
  const { t } = useTranslation(['expenses', 'common', 'categories']);
  const translateCategory = getCategoryTranslator(t);
  const [localFilters, setLocalFilters] = useState(filters);
  const [timePreset, setTimePreset] = useState('');

  useEffect(() => {
    setLocalFilters(filters);
    // Determine time preset from dates
    if (filters.dateFrom || filters.dateTo) {
      setTimePreset('custom');
    } else {
      setTimePreset('');
    }
  }, [filters, isOpen]);

  const handleCategoryToggle = (categoryId) => {
    const current = localFilters.categories || [];
    if (current.includes(categoryId)) {
      setLocalFilters({ ...localFilters, categories: current.filter(c => c !== categoryId) });
    } else {
      setLocalFilters({ ...localFilters, categories: [...current, categoryId] });
    }
  };

  const handleCurrencyToggle = (currency) => {
    const current = localFilters.currencies || [];
    if (current.includes(currency)) {
      setLocalFilters({ ...localFilters, currencies: current.filter(c => c !== currency) });
    } else {
      setLocalFilters({ ...localFilters, currencies: [...current, currency] });
    }
  };

  const handleTimePresetChange = (preset) => {
    setTimePreset(preset);
    
    const now = new Date();
    let dateFrom = null;
    let dateTo = null;

    if (preset === 'today') {
      const startOfDay = new Date(now.getFullYear(), now.getMonth(), now.getDate());
      dateFrom = Math.floor(startOfDay.getTime() / 1000);
      dateTo = Math.floor(now.getTime() / 1000);
    } else if (preset === 'week') {
      const startOfWeek = new Date(now);
      startOfWeek.setDate(now.getDate() - now.getDay());
      startOfWeek.setHours(0, 0, 0, 0);
      dateFrom = Math.floor(startOfWeek.getTime() / 1000);
      dateTo = Math.floor(now.getTime() / 1000);
    } else if (preset === 'month') {
      const startOfMonth = new Date(now.getFullYear(), now.getMonth(), 1);
      dateFrom = Math.floor(startOfMonth.getTime() / 1000);
      dateTo = Math.floor(now.getTime() / 1000);
    } else if (preset === 'year') {
      const startOfYear = new Date(now.getFullYear(), 0, 1);
      dateFrom = Math.floor(startOfYear.getTime() / 1000);
      dateTo = Math.floor(now.getTime() / 1000);
    } else if (preset === 'custom') {
      // Keep existing custom dates or clear
      dateFrom = localFilters.dateFrom;
      dateTo = localFilters.dateTo;
    } else {
      // Clear dates
      dateFrom = null;
      dateTo = null;
    }

    setLocalFilters({ ...localFilters, dateFrom, dateTo });
  };

  const handleCustomDateChange = (field, value) => {
    if (value) {
      const date = new Date(value);
      if (field === 'dateFrom') {
        date.setHours(0, 0, 0, 0);
      } else {
        date.setHours(23, 59, 59, 999);
      }
      setLocalFilters({ ...localFilters, [field]: Math.floor(date.getTime() / 1000) });
    } else {
      setLocalFilters({ ...localFilters, [field]: null });
    }
  };

  const formatDateForInput = (timestamp) => {
    if (!timestamp) return '';
    const date = new Date(timestamp * 1000);
    return date.toISOString().split('T')[0];
  };

  const handleApply = () => {
    onApply(localFilters);
    onClose();
  };

  const handleClear = () => {
    const clearedFilters = {
      search: '',
      categories: [],
      currencies: [],
      dateFrom: null,
      dateTo: null,
      amountMin: null,
      amountMax: null,
    };
    setLocalFilters(clearedFilters);
    setTimePreset('');
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-end sm:items-center justify-center p-4 bg-black/50">
      <div className="card max-w-lg w-full max-h-[85vh] overflow-y-auto">
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-2xl font-black">{t('filters.title', 'Filters')}</h2>
          <button
            onClick={onClose}
            className="text-2xl hover:opacity-70"
          >
            ✕
          </button>
        </div>

        {/* Search */}
        <div className="mb-4">
          <label className="block text-sm font-bold mb-2">{t('filters.searchByTitle', 'Search by Title')}</label>
          <input
            type="text"
            value={localFilters.search || ''}
            onChange={(e) => setLocalFilters({ ...localFilters, search: e.target.value })}
            placeholder={t('filters.searchPlaceholder', 'Search expenses...')}
            className="w-full px-3 py-2 border-2 border-black rounded-lg font-bold focus:outline-none focus:ring-2 focus:ring-primary"
          />
        </div>

        {/* Categories */}
        <div className="mb-4">
          <label className="block text-sm font-bold mb-2">{t('filters.categories', 'Categories')}</label>
          <div className="flex flex-wrap gap-2">
            {categories.map((cat) => (
              <button
                key={cat.id}
                onClick={() => handleCategoryToggle(cat.id)}
                className={`px-3 py-1 border-2 border-black rounded-lg text-sm font-bold transition-colors ${
                  (localFilters.categories || []).includes(cat.id)
                    ? 'bg-primary text-black'
                    : 'bg-white hover:bg-gray-100'
                }`}
              >
                {cat.icon} {translateCategory(cat.name)}
              </button>
            ))}
          </div>
        </div>

        {/* Currencies */}
        <div className="mb-4">
          <label className="block text-sm font-bold mb-2">{t('filters.currencies', 'Currencies')}</label>
          <div className="flex flex-wrap gap-2">
            {CURRENCIES.map((currency) => (
              <button
                key={currency}
                onClick={() => handleCurrencyToggle(currency)}
                className={`px-3 py-1 border-2 border-black rounded-lg text-sm font-bold transition-colors ${
                  (localFilters.currencies || []).includes(currency)
                    ? 'bg-primary text-black'
                    : 'bg-white hover:bg-gray-100'
                }`}
              >
                {currency}
              </button>
            ))}
          </div>
        </div>

        {/* Time Range */}
        <div className="mb-4">
          <label className="block text-sm font-bold mb-2">{t('filters.timeRange', 'Time Range')}</label>
          <div className="flex flex-wrap gap-2 mb-3">
            {TIME_PRESET_KEYS.map((preset) => (
              <button
                key={preset}
                onClick={() => handleTimePresetChange(preset)}
                className={`px-3 py-1 border-2 border-black rounded-lg text-sm font-bold transition-colors ${
                  timePreset === preset
                    ? 'bg-primary text-black'
                    : 'bg-white hover:bg-gray-100'
                }`}
              >
                {t(`filters.timePresets.${preset}`, preset)}
              </button>
            ))}
          </div>
          
          {/* Custom Date Pickers */}
          {timePreset === 'custom' && (
            <div className="flex gap-2">
              <div className="flex-1">
                <label className="block text-xs font-bold mb-1">{t('filters.from', 'From')}</label>
                <input
                  type="date"
                  value={formatDateForInput(localFilters.dateFrom)}
                  onChange={(e) => handleCustomDateChange('dateFrom', e.target.value)}
                  className="w-full px-2 py-1 border-2 border-black rounded-lg text-sm font-bold focus:outline-none focus:ring-2 focus:ring-primary"
                />
              </div>
              <div className="flex-1">
                <label className="block text-xs font-bold mb-1">{t('filters.to', 'To')}</label>
                <input
                  type="date"
                  value={formatDateForInput(localFilters.dateTo)}
                  onChange={(e) => handleCustomDateChange('dateTo', e.target.value)}
                  className="w-full px-2 py-1 border-2 border-black rounded-lg text-sm font-bold focus:outline-none focus:ring-2 focus:ring-primary"
                />
              </div>
            </div>
          )}
        </div>

        {/* Amount Range */}
        <div className="mb-6">
          <label className="block text-sm font-bold mb-2">{t('filters.amountRange', 'Amount Range (USD equivalent)')}</label>
          <div className="flex gap-2">
            <div className="flex-1">
              <input
                type="number"
                value={localFilters.amountMin || ''}
                onChange={(e) => setLocalFilters({ ...localFilters, amountMin: e.target.value ? parseFloat(e.target.value) : null })}
                placeholder="Min"
                className="w-full px-3 py-2 border-2 border-black rounded-lg font-bold focus:outline-none focus:ring-2 focus:ring-primary"
              />
            </div>
            <span className="flex items-center font-bold">-</span>
            <div className="flex-1">
              <input
                type="number"
                value={localFilters.amountMax || ''}
                onChange={(e) => setLocalFilters({ ...localFilters, amountMax: e.target.value ? parseFloat(e.target.value) : null })}
                placeholder="Max"
                className="w-full px-3 py-2 border-2 border-black rounded-lg font-bold focus:outline-none focus:ring-2 focus:ring-primary"
              />
            </div>
          </div>
        </div>

        {/* Action Buttons */}
        <div className="flex gap-2">
          <button
            onClick={handleClear}
            className="flex-1 px-4 py-2 border-2 border-black rounded-lg font-bold bg-white hover:bg-gray-100 transition-colors"
          >
            {t('filters.clearAll', 'Clear All')}
          </button>
          <button
            onClick={handleApply}
            className="flex-1 px-4 py-2 border-2 border-black rounded-lg font-bold bg-primary text-black shadow-hard-sm hover:opacity-90 transition-opacity"
          >
            {t('filters.apply', 'Apply Filters')}
          </button>
        </div>
      </div>
    </div>
  );
}

export default FilterPanel;
